import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './ShopifySalesReport.css';

const Toast = ({ message, type, onClose }) => (
  <div className={`toast ${type}`}> 
    {message}
    <button className="toast-close" onClick={onClose}>×</button>
  </div>
);

const ShopifySalesReport = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });

  useEffect(() => {
    // Set default date range to last 30 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ message: '', type: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const generateReport = async () => {
    if (!startDate || !endDate) {
      setToast({ message: 'Please select both start and end dates', type: 'error' });
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setToast({ message: 'Start date must be before end date', type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiCall('/api/shopify-sales/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate
        })
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data.report_data);
        setSummary(data.summary);
        setToast({ 
          message: `Report generated successfully! Found ${data.report_data.length} products.`, 
          type: 'success' 
        });
      } else {
        const errorData = await response.json();
        setToast({ message: errorData.error || 'Failed to generate report', type: 'error' });
      }
    } catch (e) {
      setToast({ message: 'Error generating report', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="shopify-sales-report">
      {toast.message && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />
      )}
      
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Shopify Sales Report</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Select a date range to generate a sales report</li>
              <li>Report shows sales by product in descending order of revenue</li>
              <li>Data is pulled from the shopify_sales table</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="section">
          <h2>Generate Sales Report</h2>
          
          <div className="date-range-selector">
            <label htmlFor="startDate">Start Date:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            
            <label htmlFor="endDate">End Date:</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            
            <button 
              className="generate-button" 
              onClick={generateReport}
              disabled={isLoading}
            >
              {isLoading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>

          {isLoading && (
            <div className="loading">Generating report...</div>
          )}

          {summary && (
            <div className="report-summary">
              <h3>Report Summary</h3>
              <div className="summary-stats">
                <div className="summary-stat">
                  <div className="summary-stat-label">Date Range</div>
                  <div className="summary-stat-value">{summary.date_range}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">Total Products</div>
                  <div className="summary-stat-value">{formatNumber(summary.total_products)}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">Total Revenue</div>
                  <div className="summary-stat-value">{formatCurrency(summary.total_revenue)}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-stat-label">Total Quantity</div>
                  <div className="summary-stat-value">{formatNumber(summary.total_quantity)}</div>
                </div>
              </div>
            </div>
          )}

          {reportData.length > 0 && (
            <div className="sales-table-container">
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product Title</th>
                    <th>Vendor</th>
                    <th>Quantity Sold</th>
                    <th>Total Revenue</th>
                    <th>Average Price</th>
                    <th>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((item, index) => (
                    <tr key={`${item.sku}-${index}`}>
                      <td className="sku-column">{item.sku || 'N/A'}</td>
                      <td className="title-column">{item.title || 'N/A'}</td>
                      <td className="vendor-column">{item.vendor || 'N/A'}</td>
                      <td className="quantity-column">{formatNumber(item.total_quantity)}</td>
                      <td className="revenue-column">{formatCurrency(item.total_revenue)}</td>
                      <td className="price-column">{formatCurrency(item.avg_price)}</td>
                      <td className="orders-column">{formatNumber(item.total_orders)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && reportData.length === 0 && summary && (
            <div className="no-data">
              No sales data found for the selected date range.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopifySalesReport; 